import { NoteRepository } from '../data/db'
import type { Note } from '../domain/note'
import type { SettingsRecord } from '../domain/settings'
import {
  LegacyImageClientRevisionError,
  NoteLimitExceededError,
  SettingsSyncUnavailableError,
  type RemoteSyncInventory,
  type SyncTransport,
} from './syncTransport'
import { APP_SETTINGS } from '../config/appSettings'
import { PlanLimitError } from '../auth/planLimits'

export interface SyncCoordinatorRequest {
  push: boolean
  pullNotes: boolean
  pullSettings: boolean
}

export interface SyncSummary {
  pushed: number
  pulled: number
  notePullComplete: boolean
  changedNotes: Note[]
  changedSettings: SettingsRecord[]
  settingsSyncAvailable: boolean
  accountLimitBlocks: AccountLimitBlock[]
}

export interface AccountLimitBlock {
  accountType: string
  limit: number
  noteId: string
  usage: number
}

export class SyncAccountMismatchError extends Error {
  constructor() {
    super('This browser already contains notes connected to a different account. Export or clear local data before changing accounts.')
    this.name = 'SyncAccountMismatchError'
  }
}

export class SyncEnvironmentMismatchError extends Error {
  constructor() {
    super('This browser already contains notes connected to a different app environment. Use a separate origin or clear local data before switching environments.')
    this.name = 'SyncEnvironmentMismatchError'
  }
}

export class SyncCoordinator {
  private activeSync: Promise<SyncSummary> | null = null
  private readonly activeNoteResolutions = new Map<string, Promise<Note | null>>()
  private settingsSyncAvailable = true

  constructor(
    private readonly repository: NoteRepository,
    private readonly transport: SyncTransport,
    private readonly environment: 'development' | 'production',
  ) {}

  syncOnce(
    userId: string,
    request: SyncCoordinatorRequest = { push: true, pullNotes: true, pullSettings: true },
  ): Promise<SyncSummary> {
    this.activeSync ??= this.run(userId, request).finally(() => {
      this.activeSync = null
    })
    return this.activeSync
  }

  resolveNote(userId: string, noteId: string): Promise<Note | null> {
    const key = `${userId}:${noteId}`
    const current = this.activeNoteResolutions.get(key)
    if (current) return current
    const resolution = this.runNoteResolution(userId, noteId).finally(() => {
      if (this.activeNoteResolutions.get(key) === resolution) {
        this.activeNoteResolutions.delete(key)
      }
    })
    this.activeNoteResolutions.set(key, resolution)
    return resolution
  }

  async getRemoteInventory(userId: string): Promise<RemoteSyncInventory> {
    await this.ensureBinding(userId)
    if (!this.transport.getInventory) {
      throw new Error('This synchronization server cannot verify a complete account restore.')
    }
    return this.transport.getInventory()
  }

  async resetNoteRestoreCursor(userId: string): Promise<void> {
    await this.ensureBinding(userId)
    await this.repository.deleteMeta(`sync-cursor:${this.environment}:${userId}`)
  }

  private async ensureBinding(userId: string): Promise<void> {
    const storedEnvironment = await this.repository.getMeta('sync-environment')
    if (typeof storedEnvironment === 'string' && storedEnvironment !== this.environment) {
      throw new SyncEnvironmentMismatchError()
    }
    if (!storedEnvironment) await this.repository.setMeta('sync-environment', this.environment)

    const ownerKey = 'sync-owner'
    const owner = await this.repository.getMeta(ownerKey)
    if (typeof owner === 'string' && owner !== userId) throw new SyncAccountMismatchError()
    if (!owner) await this.repository.setMeta(ownerKey, userId)
  }

  private async runNoteResolution(userId: string, noteId: string): Promise<Note | null> {
    await this.ensureBinding(userId)
    const operations = await this.repository.listNoteOutbox(noteId)
    for (const operation of operations) {
      const revision = await this.repository.getRevision(operation.revisionId)
      if (!revision) {
        await this.repository.markOperationFailed(
          operation.id,
          operation.entityId,
          'missing-local-revision',
        )
        continue
      }
      try {
        const receipt = await this.transport.pushNote(
          revision.snapshot,
          operation.id,
          revision.clientSchemaVersion,
        )
        await this.repository.applyRemoteNote(receipt.note)
        await this.repository.markOperationSynced(
          operation.id,
          operation.entityId,
          operation.revisionId,
        )
      } catch (error) {
        if (error instanceof LegacyImageClientRevisionError) {
          await this.repository.discardRejectedNoteRevision(
            operation.id,
            operation.entityId,
            operation.revisionId,
          )
          continue
        }
        if (error instanceof NoteLimitExceededError || error instanceof PlanLimitError) {
          await this.repository.markOperationBlockedByAccountLimit(
            operation.id,
            operation.entityId,
            error.limit,
            error.usage,
          )
          continue
        }
        const message = error instanceof Error ? error.message : 'sync-push-failed'
        await this.repository.markOperationFailed(operation.id, operation.entityId, message)
        throw error
      }
    }

    const remote = await this.transport.getNote(noteId)
    if (remote) return this.repository.applyRemoteNote(remote)
    return (await this.repository.getNote(noteId)) ?? null
  }

  private async run(userId: string, request: SyncCoordinatorRequest): Promise<SyncSummary> {
    await this.ensureBinding(userId)

    let pushed = 0
    let requiresFullNotePull = false
    const changedNotes = new Map<string, Note>()
    const changedSettings = new Map<string, SettingsRecord>()
    const accountLimitBlocks: AccountLimitBlock[] = []
    const operations = request.push ? await this.repository.listOutbox() : []
    for (const operation of operations) {
      try {
        if (operation.entityType === 'note') {
          const revision = await this.repository.getRevision(operation.revisionId)
          if (!revision) {
            await this.repository.markOperationFailed(
              operation.id,
              operation.entityId,
              'missing-local-revision',
            )
            continue
          }
          const receipt = await this.transport.pushNote(
            revision.snapshot,
            operation.id,
            revision.clientSchemaVersion,
          )
          await this.repository.applyRemoteNote(receipt.note)
          await this.repository.markOperationSynced(
            operation.id,
            operation.entityId,
            operation.revisionId,
          )
          const synchronized = await this.repository.getNote(operation.entityId)
          if (synchronized) changedNotes.set(synchronized.id, synchronized)
        } else {
          if (!this.settingsSyncAvailable) continue
          const settings = await this.repository.getSetting(operation.entityId)
          if (!settings || settings.revisionId !== operation.revisionId) {
            await this.repository.markSettingsOperationFailed(
              operation.id,
              operation.entityId,
              'missing-local-settings-revision',
            )
            continue
          }
          const receipt = await this.transport.pushSettings(settings, operation.id)
          await this.repository.applyRemoteSettings(receipt.settings)
          await this.repository.markSettingsOperationSynced(
            operation.id,
            operation.entityId,
            operation.revisionId,
          )
          const synchronized = await this.repository.getSetting(operation.entityId)
          if (synchronized) changedSettings.set(synchronized.key, synchronized)
        }
        pushed += 1
      } catch (error) {
        if (error instanceof LegacyImageClientRevisionError && operation.entityType === 'note') {
          await this.repository.discardRejectedNoteRevision(
            operation.id,
            operation.entityId,
            operation.revisionId,
          )
          requiresFullNotePull = true
          continue
        }
        if (error instanceof NoteLimitExceededError && operation.entityType === 'note') {
          await this.repository.markOperationBlockedByAccountLimit(
            operation.id,
            operation.entityId,
            error.limit,
            error.usage,
          )
          accountLimitBlocks.push({
            accountType: error.accountType,
            limit: error.limit,
            noteId: operation.entityId,
            usage: error.usage,
          })
          continue
        }
        if (error instanceof PlanLimitError && operation.entityType === 'note') {
          await this.repository.markOperationBlockedByAccountLimit(
            operation.id,
            operation.entityId,
            error.limit,
            error.usage,
          )
          continue
        }
        if (error instanceof SettingsSyncUnavailableError) {
          this.settingsSyncAvailable = false
          continue
        }
        const message = error instanceof Error ? error.message : 'sync-push-failed'
        if (operation.entityType === 'note') {
          await this.repository.markOperationFailed(operation.id, operation.entityId, message)
        } else {
          await this.repository.markSettingsOperationFailed(operation.id, operation.entityId, message)
        }
        throw error
      }
    }

    const settingsPull = request.pullSettings && this.settingsSyncAvailable
      ? this.transport.pullSettings()
          .then((settings) => ({ settings, error: null as unknown }))
          .catch((error: unknown) => ({ settings: [] as SettingsRecord[], error }))
      : Promise.resolve({ settings: [] as SettingsRecord[], error: null as unknown })
    let pulled = 0
    let notePullComplete = !(request.pullNotes || requiresFullNotePull)
    if (request.pullNotes || requiresFullNotePull) {
      const cursorKey = `sync-cursor:${this.environment}:${userId}`
      let cursor = requiresFullNotePull ? null : await this.repository.getMeta(cursorKey)
      for (let page = 0; page < APP_SETTINGS.sync.maxPullPages; page += 1) {
        const result = await this.transport.pullNotes(typeof cursor === 'string' ? cursor : null)
        for (const note of result.notes) {
          const applied = await this.repository.applyRemoteNote(note)
          changedNotes.set(applied.id, applied)
        }
        cursor = result.cursor
        await this.repository.setMeta(cursorKey, cursor)
        pulled += result.notes.length
        if (result.notes.length < APP_SETTINGS.sync.pullPageSize) {
          notePullComplete = true
          break
        }
      }
    }

    const settingsResult = await settingsPull
    if (settingsResult.error instanceof SettingsSyncUnavailableError) {
      this.settingsSyncAvailable = false
    } else if (settingsResult.error) {
      throw settingsResult.error
    }
    for (const settings of settingsResult.settings) {
      const applied = await this.repository.applyRemoteSettings(settings)
      changedSettings.set(applied.key, applied)
    }

    return {
      pushed,
      pulled,
      notePullComplete,
      changedNotes: [...changedNotes.values()],
      changedSettings: [...changedSettings.values()],
      settingsSyncAvailable: this.settingsSyncAvailable,
      accountLimitBlocks,
    }
  }
}
